import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericModal from "shared/components/GenericModal";
import SubscriptionPreviewBlock from "./SubscriptionPreviewBlock";
import Button from "shared/Button";
import { Redirect } from "react-router-dom";
import { Application, Spinner } from "react-rainbow-components";
import { updateFrequency, updateSubscriptionProducts } from "./actions";
import { downgrade60PodSku } from "shared/utility";

const DowngradeFrom60PodModal = ({ onClose, subscription, show }) => {
  const dispatch = useDispatch();
  const skuMap = useSelector(s => s.config.planConfigs.GB?.skuMap);

  const [isDowngrading, setIsDowngrading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [redirect, setRedirect] = useState(null);

  const onDowngradeSubscription = useCallback(() => {
    setIsDowngrading(true);

    dispatch(
      updateSubscriptionProducts(subscription.id, {
        products: subscription.products.map(({ id, variant, quantity }) => {
          const downgradedSku = downgrade60PodSku(variant.sku);
          const newVariantSummary = skuMap[downgradedSku];

          return {
            id: id,
            product_id: newVariantSummary.item.product_id,
            variant_id: newVariantSummary.item.variant_id,
            quantity
          };
        })
      })
    )
      .then(
        () =>
          dispatch(
            updateFrequency(subscription.id, {
              interval: Math.ceil(subscription.interval / 2)
            })
          ),
        err => {
          setIsDowngrading(false);
          setErrorText(err);
        }
      )
      .then(() => {
        onClose();
      })
      .finally(() => setIsDowngrading(false));
  }, [dispatch, onClose, skuMap, subscription]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (!subscription) {
    return null;
  }

  return (
    <GenericModal
      className="modal-dialog-centered"
      onClose={onClose}
      show={show}
    >
      <div className="modal-header">
        <h4 className="body-20 fw-bold">
          You've been upgraded to our 60-pod box subscription.
        </h4>
        <button
          aria-label="Close"
          className="btn-close"
          onClick={onClose}
          type="button"
        />
      </div>
      <div className="modal-body">
        <p className="body-15">
          After your next order, you'll start receiving your deliveries at half
          the delivery frequency. This will be at a reduced price of £25, versus
          what would have been the previous price of £27 (£13.50 per refill
          pack).
        </p>
        <p className="body-15 mt-3">
          Your deliveries will still be sent in letterbox friendly packaging -
          so you don't have to worry about missing a delivery.
        </p>
        <hr className="border-top mt-5 mb-3 opacity-75" />
        <p className="body-17 fw-medium">Your updated order</p>
        <SubscriptionPreviewBlock
          className="mt-2"
          subscription={subscription}
        />
        <hr className="border-top mb-3 mt-4 opacity-75" />
        <Button
          className="w-100"
          onClick={() => {
            setRedirect(`/subscriptions/${subscription.id}`);
          }}
          size="md"
        >
          View my subscription
        </Button>
        {!isDowngrading ? (
          <button
            className="w-100 mt-2 border-0 bg-white body-15 fw-medium py-2"
            onClick={onDowngradeSubscription}
            type="button"
          >
            <u>I want to go back to my 30 pod subscription</u>
          </button>
        ) : (
          <div
            className="d-block position-relative mt-2"
            style={{ height: "40px" }}
          >
            <Application
              theme={{
                rainbow: {
                  palette: {
                    charcoal: "#2d2926"
                  }
                }
              }}
            >
              <Spinner variant="charcoal" type="arc" size="x-small" />
            </Application>
          </div>
        )}

        {errorText && (
          <p className="body-15 text-center text-danger mt-3">{errorText}</p>
        )}
      </div>
    </GenericModal>
  );
};

export default DowngradeFrom60PodModal;
